import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { ArrowLeft, LogOut, Menu, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { DEV_BYPASS, signOut } from "@/lib/clerk";
import { HOME_URL } from "@/lib/home";
import { displayName, type Me } from "@/lib/me";
import { useTheme } from "@/lib/theme";
import { useMenu } from "@/lib/useMenu";

const navClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap transition-colors",
    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
  );

const menuItemClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
    isActive ? "bg-accent text-accent-foreground font-medium" : "text-foreground hover:bg-secondary",
  );

const signOutAndLeave = () => {
  void signOut().finally(() => window.location.assign("/"));
};

/** The links a role may see, in order. The routes themselves are gated in App. */
const pagesFor = (me: Me) => [
  { to: "/", label: "My briefs", end: true },
  ...(me.isAdmin ? [{ to: "/review", label: "Submissions", end: false }] : []),
  ...(me.isSuperadmin ? [{ to: "/admin", label: "Admin", end: false }] : []),
];

/**
 * Below `md` the page links and Sign out sit in one menu: scrolling them inside
 * the header left Admin and Sign out off the right edge with nothing to say so.
 */
function PhoneMenu({ me }: { me: Me }) {
  const menu = useMenu();
  const { pathname } = useLocation();
  const { close } = menu;
  // Navigating by any route (back button included) closes it.
  useEffect(() => close(), [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={menu.ref} className="relative md:hidden">
      <Button variant="ghost" size="icon" aria-label="Menu" title="Menu" {...menu.buttonProps}>
        <Menu size={18} />
      </Button>
      {menu.open ? (
        <div role="menu" aria-label="Pages" className="bg-card border-border absolute top-full right-0 z-30 mt-2 w-56 max-w-[calc(100vw-2rem)] rounded-lg border p-1 shadow-xl">
          <div className="border-border mb-1 border-b px-2.5 pt-1.5 pb-2 leading-tight">
            <span className="block truncate text-sm">{displayName(me)}</span>
            <span className="text-muted-foreground block text-[11px]">{me.role}</span>
          </div>
          {pagesFor(me).map((p) => (
            <NavLink key={p.to} to={p.to} end={p.end} role="menuitem" className={menuItemClass} onClick={menu.close}>
              {p.label}
            </NavLink>
          ))}
          {!DEV_BYPASS ? (
            <button
              type="button"
              role="menuitem"
              className={cn(menuItemClass({ isActive: false }), "border-border mt-1 border-t pt-2")}
              onClick={() => {
                menu.close();
                signOutAndLeave();
              }}
            >
              <LogOut size={14} className="shrink-0" /> Sign out
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The feedback entry point sits directly next to the account area (house
 * skill); App mounts the button in that slot so this header stays router-free.
 */
export function Header({ me, feedbackSlot }: { me: Me; feedbackSlot?: React.ReactNode }) {
  const { theme, toggle } = useTheme();
  return (
    <header className="border-border bg-card/80 sticky top-0 z-20 border-b backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-2 px-4 sm:gap-3">
        <a
          href={HOME_URL}
          aria-label="Back to Home"
          className="text-muted-foreground hover:text-foreground -ml-1.5 flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1.5 text-sm whitespace-nowrap transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="hidden md:inline">Home</span>
        </a>
        <Link to="/" className="flex shrink-0 items-center gap-2.5 font-semibold tracking-tight">
          <img src="/logo.svg" alt="" width={26} height={26} className="rounded-md" />
          <span className="hidden sm:inline">Solution Brief</span>
        </Link>
        <nav aria-label="Pages" className="ml-2 hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] md:flex [&::-webkit-scrollbar]:hidden">
          {pagesFor(me).map((p) => (
            <NavLink key={p.to} to={p.to} end={p.end} className={navClass}>
              {p.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {feedbackSlot}
          <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
          <div className="ml-1 hidden flex-col items-end leading-tight md:flex">
            <span className="text-sm">{displayName(me)}</span>
            <span className="text-muted-foreground text-[11px]">{me.role}</span>
          </div>
          {!DEV_BYPASS ? (
            <Button variant="ghost" size="icon" aria-label="Sign out" className="hidden md:inline-flex" onClick={signOutAndLeave}>
              <LogOut size={16} />
            </Button>
          ) : null}
          <PhoneMenu me={me} />
        </div>
      </div>
    </header>
  );
}
